import * as React from 'react';
import { Box, Typography } from '@material-ui/core';
import { CustomButton } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import { CaseCalculationPart } from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { CalculationTotals } from '../types/alternative-calculation.types';
import { useAppSelector } from '../../../store/configureStore';
import { useTranslate } from '../../../hooks/useTranslate';
import { useVariantsEnabled } from '../context/CalculationFlagsContext';
import { displayCurrency, offerVariants, selectedAlternatives } from '../utils/calculation.utils';

export interface AlternativeCalculationFooterProps {
  parts: CaseCalculationPart[];
  totals: CalculationTotals;
  currency: IReferential | null;
  disabled: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AlternativeCalculationFooter: React.FC<AlternativeCalculationFooterProps> = ({
  parts,
  totals,
  currency,
  disabled,
  isLoading,
  onClose,
  onSave
}) => {
  const c = useStyles();
  const t = useTranslate();
  const country = useAppSelector((s) => s.app?.appUser?.organization?.country ?? null);
  const variantsEnabled = useVariantsEnabled();
  const currencyName = currency?.displayName ?? '';
  const totalParts = parts.length;
  const selectedParts = parts.filter((p) => selectedAlternatives(p).length).length;
  const totalVariants = parts.reduce((s, p) => s + offerVariants(p).length, 0);
  const totalVariantsLabel = variantsEnabled
    ? totalVariants === 1
      ? t('ALT_CALC_VARIANTS_TOTAL_ONE', 'variantă în total')
      : t('ALT_CALC_VARIANTS_TOTAL', 'variante în total')
    : totalVariants === 1
    ? t('ALT_CALC_SELECTIONS_TOTAL_ONE', 'selecție în total')
    : t('ALT_CALC_SELECTIONS_TOTAL', 'selecții în total');
  const saving = `${totals.savingValue < 0 ? '−' : ''}${displayCurrency(
    Math.abs(totals.savingValue),
    country,
    currencyName
  )}`;

  const totalsList: Array<{ label: string; value: string; positive?: boolean }> = [
    {
      label: t('ALT_CALC_FOOT_PARTS', 'Total piese alternativă'),
      value: displayCurrency(totals.alternativePartsAmount, country, currencyName)
    },
    {
      label: t('ALT_CALC_FOOT_TOTAL', 'Total calculație alternativă'),
      value: displayCurrency(totals.alternativeAmountWithoutTVA, country, currencyName)
    },
    {
      label: t('ALT_CALC_FOOT_SAVING', 'Economisire'),
      value: saving,
      positive: totals.savingValue > 0
    }
  ];

  return (
    <Box className={c.footBar}>
      <Box className={cx(c.row, c.wrap, c.gap24)}>
        {totalsList.map((item) => (
          <Box key={item.label}>
            <Typography variant="caption" display="block" className={cx(c.wideUpper, c.faint)}>
              {item.label}
            </Typography>
            <Typography
              variant="subtitle1"
              className={cx(c.bold, c.tabular, item.positive && c.successText)}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" className={cx(c.muted, c.pushRight)}>
        {`${selectedParts}/${totalParts} ${
          variantsEnabled
            ? t('ALT_CALC_PARTS_WITH_VARIANTS', 'piese cu variante selectate')
            : t('ALT_CALC_PARTS_WITH_SELECTIONS', 'piese cu selecții')
        } · ${totalVariants} ${totalVariantsLabel}`}
      </Typography>

      {onClose && (
        <CustomButton variant="outlined" size="large" onClick={onClose}>
          {t('ALT_CALC_CANCEL', 'Renunță')}
        </CustomButton>
      )}
      <CustomButton size="large" onClick={onSave} disabled={disabled} isLoading={isLoading}>
        {t('ALT_CALC_SAVE', 'Salvează calculația alternativă')}
      </CustomButton>
    </Box>
  );
};

export default AlternativeCalculationFooter;
